import { type KeyboardEvent, type ReactNode, useRef } from "react";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";

export interface TabItem<T extends string> {
  id: T;
  label: string;
  icon?: ReactNode;
}

interface TabNavProps<T extends string> {
  tabs: TabItem<T>[];
  activeTab: T;
  onTabChange: (tab: T) => void;
  /** Accessible name of the tab list. Default "Profile sections". */
  label?: string;
  className?: string;
}

/**
 * Pill tabs on a frosted tray (docs/v2-core/STYLE.md). Follows the WAI-ARIA
 * tabs pattern: one tab stop (the selected tab), Arrow Left/Right move and
 * select, Home/End jump to the ends. The tray wraps rather than scrolling
 * sideways, so every section stays visible at 390px.
 */
export function TabNav<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  label = "Profile sections",
  className,
}: TabNavProps<T>) {
  const refs = useRef(new Map<T, HTMLButtonElement>());

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = tabs.findIndex((t) => t.id === activeTab);
    if (current === -1) return;
    const last = tabs.length - 1;
    const next =
      event.key === "ArrowRight"
        ? (current + 1) % tabs.length
        : event.key === "ArrowLeft"
          ? (current - 1 + tabs.length) % tabs.length
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    const id = tabs[next]!.id;
    onTabChange(id);
    refs.current.get(id)?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cn(
        "mt-8 flex flex-wrap gap-1 rounded-[1.75rem] bg-white/60 p-1 shadow-soft ring-1 ring-white/80 backdrop-blur sm:inline-flex sm:p-1.5",
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              if (el) refs.current.set(tab.id, el);
              else refs.current.delete(tab.id);
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            id={`tab-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              // Compact on a phone, so a 7-tab profile wraps to three rows, not four.
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition sm:gap-2 sm:px-4 sm:py-2",
              FOCUS_RING,
              isActive
                ? "bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-soft"
                : "text-neutral-700 hover:bg-white hover:text-sky-700",
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

interface TabPanelProps {
  id: string;
  activeTab: string;
  children: ReactNode;
}

export function TabPanel({ id, activeTab, children }: TabPanelProps) {
  if (id !== activeTab) return null;
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${id}`}
      aria-labelledby={`tab-${id}`}
      className="mt-8"
    >
      {children}
    </div>
  );
}
